import { useQuery } from "@tanstack/react-query";

import { LockIcon } from "lucide-react";

import { TicketHistoryList } from "@/features/tickets/components/TicketHistoryList";
import { TicketHistorySkeleton } from "@/features/tickets/components/TicketHistorySkeleton";
import { getTicketHistory } from "@/features/tickets/services/ticket";
import { EmptyState } from "@/shared/components/EmptyState";
import Error from "@/shared/components/Error";
import { InlineError } from "@/shared/components/InlineError";
import { useErrorModal } from "@/shared/hooks/useErrorModal";
import { QUERY_EXPIRATIONS, QUERY_KEYS } from "@/shared/lib/constants/queries";
import { AppError } from "@/shared/lib/utils/errors";

interface TicketHistoryPanelProps {
  projectId: string;
  ticketId: string;
  /** Whether the caller has PROJECT_TICKET_HISTORY_READ for this project. */
  canReadHistory: boolean;
  /** Gates the fetch until the History tab has actually been opened. */
  enabled: boolean;
}

export function TicketHistoryPanel({ projectId, ticketId, canReadHistory, enabled }: TicketHistoryPanelProps) {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: [QUERY_KEYS.GET_TICKET_HISTORY, projectId, ticketId],
    queryFn: () => getTicketHistory(projectId, ticketId),
    staleTime: QUERY_EXPIRATIONS.GET_TICKET_HISTORY,
    enabled: canReadHistory && enabled,
  });

  const { open: errorModalOpen, setOpen: setErrorModalOpen, handleRetry } = useErrorModal(error, refetch);

  if (!canReadHistory) {
    return <EmptyState icon={LockIcon} title="No access" description="You don't have permission to view this ticket's history." className="py-10" />;
  }

  return (
    <>
      {error && <Error variant="modal" title="Failed to load history" error={error as AppError | Error} open={errorModalOpen} onOpenChange={setErrorModalOpen} onRetry={handleRetry} />}

      {error && !errorModalOpen ? (
        <InlineError title="Failed to load history" description="Something went wrong while fetching this ticket's history." onRetry={handleRetry} className="py-10" />
      ) : isPending ? (
        <div className="flex flex-col divide-y">
          {Array.from({ length: 3 }).map((_, i) => (
            <TicketHistorySkeleton key={i} />
          ))}
        </div>
      ) : (
        <TicketHistoryList entries={data ?? []} />
      )}
    </>
  );
}
